/**
 * Central navigation helpers — prefer these over string literals or legacy onNav targets.
 * URL is the sole source of truth for page, tab, and shareable context.
 */

import { buildTabPath } from './module-tabs.js';
import { buildAuditoriaEventPath, buildKbNewArticlePath } from './entity-routes.js';
import { serializeSearchParams, searchParamsToString } from './search-params.js';
import {
  BILLING_SEARCH_SCHEMA,
  REPAIRS_SEARCH_SCHEMA,
  AUDITORIA_SEARCH_SCHEMA,
  INTEGRATIONS_SEARCH_SCHEMA,
  OBSERVABILIDADE_SEARCH_SCHEMA,
  PLANOS_PERMISSOES_SEARCH_SCHEMA,
  INADIMPLENCIA_SEARCH_SCHEMA,
} from './search-param-schemas.js';
import { PAGE_TO_PATH, resolveTenantId } from './paths.js';

function withTenantQuery(pathname, tenantId, schema) {
  if (tenantId == null || String(tenantId).trim() === '') return pathname;
  const search = searchParamsToString(
    serializeSearchParams({ tenantId: String(tenantId) }, schema),
  );
  return search ? `${pathname}${search}` : pathname;
}

function withRepairsQuery(pathname, { tenantId, ticketId } = {}) {
  const search = searchParamsToString(serializeSearchParams({
    tenantId: tenantId != null ? String(tenantId) : null,
    ticketId: ticketId != null ? String(ticketId) : null,
  }, REPAIRS_SEARCH_SCHEMA));
  return search ? `${pathname}${search}` : pathname;
}

/**
 * Canonical path builders used by Link / NavLink / navigate.
 */
export const paths = {
  dashboard: () => '/dashboard',
  lojas: () => '/lojas',
  novaLoja: () => '/lojas/nova',
  loja: (tenantId) => {
    const id = resolveTenantId(tenantId);
    if (!id) return '/lojas';
    return `/lojas/${encodeURIComponent(id)}`;
  },
  cockpit: (tenantId, tabId = 'overview') => {
    const id = resolveTenantId(tenantId);
    if (!id) return '/lojas';
    return buildTabPath('cockpit', tabId || 'overview', { tenantId: id });
  },
  planos: () => '/planos',
  planosPermissoes: (tenantId) => withTenantQuery(
    '/planos/permissoes',
    resolveTenantId(tenantId),
    PLANOS_PERMISSOES_SEARCH_SCHEMA,
  ),
  inadimplencia: (opts = {}) => {
    const base = opts.tab
      ? (buildTabPath('inadimplencia', opts.tab) || '/inadimplencia')
      : '/inadimplencia';
    return withTenantQuery(base, resolveTenantId(opts.tenantId), INADIMPLENCIA_SEARCH_SCHEMA);
  },
  billing: (opts = {}) => {
    const base = opts.tab
      ? (buildTabPath('billing', opts.tab) || '/billing')
      : '/billing';
    return withTenantQuery(base, resolveTenantId(opts.tenantId), BILLING_SEARCH_SCHEMA);
  },
  equipe: () => '/equipe',
  perfis: () => '/equipe/perfis',
  config: () => '/configuracoes',
  auditoria: (opts = {}) => {
    if (opts.eventId != null && String(opts.eventId).trim() !== '') {
      return buildAuditoriaEventPath(opts.eventId);
    }
    return withTenantQuery('/auditoria', resolveTenantId(opts.tenantId), AUDITORIA_SEARCH_SCHEMA);
  },
  integrations: (opts = {}) => {
    const base = opts.tab
      ? (buildTabPath('integrations', opts.tab) || '/integracoes')
      : '/integracoes';
    return withTenantQuery(base, resolveTenantId(opts.tenantId), INTEGRATIONS_SEARCH_SCHEMA);
  },
  observabilidade: (opts = {}) => {
    const base = opts.tab
      ? (buildTabPath('observabilidade', opts.tab) || '/observabilidade')
      : '/observabilidade';
    return withTenantQuery(base, resolveTenantId(opts.tenantId), OBSERVABILIDADE_SEARCH_SCHEMA);
  },
  repairs: (opts = {}) => {
    const base = opts.tab
      ? (buildTabPath('repairs', opts.tab) || '/repairs')
      : '/repairs';
    return withRepairsQuery(base, {
      tenantId: resolveTenantId(opts.tenantId),
      ticketId: opts.ticketId,
    });
  },
  tickets: (opts = {}) => {
    const base = opts.tab
      ? (buildTabPath('tickets', opts.tab) || '/suporte/tickets')
      : '/suporte/tickets';
    // No by-id API yet — ticketId stays as optional query for list lookup behind MVP guard.
    if (opts.ticketId == null || String(opts.ticketId).trim() === '') return base;
    const q = `ticketId=${encodeURIComponent(String(opts.ticketId).trim())}`;
    return `${base}?${q}`;
  },
  knowledgeBase: (opts = {}) => {
    if (opts.newArticle) {
      return buildKbNewArticlePath(opts.ticketId);
    }
    const base = opts.tab
      ? (buildTabPath('knowledge-base', opts.tab) || '/suporte/base-conhecimento')
      : '/suporte/base-conhecimento';
    return base;
  },
  supportReports: (opts = {}) => {
    if (opts.tab) return buildTabPath('support-reports', opts.tab) || '/suporte/relatorios';
    return '/suporte/relatorios';
  },
  notificacoesOps: (opts = {}) => {
    if (opts.tab) return buildTabPath('notificacoes-ops', opts.tab) || '/notificacoes';
    return '/notificacoes';
  },
  techSpec: () => '/sistema/especificacao-tecnica',
};

/**
 * Resolve sidebar / page-id to a canonical href (no tenant context).
 */
export function hrefForPage(pageId) {
  if (!pageId) return paths.dashboard();
  if (pageId === 'usuarios') return paths.equipe();
  return PAGE_TO_PATH[pageId] || paths.dashboard();
}

/**
 * Build a location descriptor ({ pathname, search }) from a full href.
 */
export function toLocation(href) {
  if (!href || typeof href !== 'string') return { pathname: '/dashboard' };
  const q = href.indexOf('?');
  if (q < 0) return { pathname: href };
  return { pathname: href.slice(0, q), search: href.slice(q) };
}
